import { useAdminStore, ADMIN_STORE_PERSIST_KEY } from "@/store";
import { clearAdminSession } from "@/lib/auth";
import { resolveSiblingAuthWrite } from "@njbsoft/store";

/**
 * Cross-tab session convergence OUTSIDE refresh moments. `storage` fires only
 * in tabs that did not write, so this is exactly "a sibling changed the
 * persisted session". The decision is `resolveSiblingAuthWrite`, shared by all
 * four portals so one rule governs every tab; this module supplies the store
 * and the portal-specific teardown. An adopted pair patches EXACTLY the token
 * triple and its stamp — never a blind rehydrate, because the MFA challenge state has its own lifecycle semantics
 * and is not this module's to move.
 *
 * The two guarded arms, decided deliberately:
 * - This tab holds no live session (`refreshToken` null): do nothing. The
 *   module's job is keeping LIVE sessions converged; a sibling's sign-IN is
 *   never adopted into a tab parked on the login screen, which would mark it
 *   authenticated under a user object it never loaded.
 * - The persisted session is gone (a sibling signed out, clearing the bag):
 *   funnel through `clearAdminSession` — the portal's forced-session-end path,
 *   which clears auth state and the query cache before hard-navigating, so a
 *   super-admin's cached org reads never outlive the session — never
 *   a partial null-patch that would leave that cache behind.
 *
 * Call once at module scope in `main.tsx`. Returns the unsubscribe for tests.
 */
export function setupSessionSync(): () => void {
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== ADMIN_STORE_PERSIST_KEY) return;
    const state = useAdminStore.getState();
    const decision = resolveSiblingAuthWrite(event.newValue, state);
    if (decision.action === "ignore") return;
    if (decision.action === "clear") {
      clearAdminSession();
      return;
    }
    // Verbatim, stamp included: the adopted pair keeps the moment the
    // sibling minted it, so this tab's next refresh compares against the
    // rotation that actually happened rather than the moment it heard.
    state.adoptTokens(decision.tokens);
  };
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener("storage", onStorage);
  };
}
